// ─────────────────────────────────────────────────────────────────────────
// Demo problem bank for the Agent Tutor workspace.
//
// The real step-checking/hint intelligence lives server-side at
// /api/agent-tutor/{start-problem,check-step,hint} (see agentTutorStorage.js).
// Those endpoints may not be deployed yet, so TutorWorkspace falls back to
// the pure-local logic in this file on a 404 or network failure — every
// function here mirrors the *shape* of the real endpoint's response so the
// workspace can't tell the difference except for a quiet inline notice.
// ─────────────────────────────────────────────────────────────────────────

// `difficulty` matches the live start-problem endpoint's semantics exactly
// (easy|medium|hard — see _DIFFICULTY_LEVELS in agent_tutor.py) so a
// component like ProblemBar's difficulty chip renders identically whether
// the problem came from the model or this offline bank. `gradeBand` is a
// separate, offline-only label (used in the example-problem picker, not the
// active-problem header) — the two used to be conflated into one field.
export const DEMO_PROBLEMS = [
  {
    id: 'two-step-1',
    topic: 'Two-step equations',
    gradeBand: 'Grades 7–8',
    difficulty: 'medium',
    prompt: 'Solve for x:',
    raw: '−2x + 6 = 12',
    latex: '-2x + 6 = 12',
    goal: 'Find the value of x.',
    opening: "Let's isolate x one operation at a time. What would you do first to both sides?",
    steps: ['−2x + 6 = 12', '−2x = 6', 'x = −3'],
    stepsLatex: ['-2x + 6 = 12', '-2x = 6', 'x = -3'],
    stepNotes: [null, 'Subtracted 6 from both sides.', 'Divided both sides by −2.'],
    hints: [
      [
        'Look at what’s being added to the term with x — that’s what you want to undo first.',
        'Try subtracting 6 from both sides of the equation.',
        'Subtract 6 from both sides: $-2x = 6$',
      ],
      [
        'You’ve isolated the x-term. Now undo the multiplication in front of x.',
        'Divide both sides by the coefficient of x — watch the sign.',
        'Divide both sides by $-2$: $x = -3$',
      ],
    ],
    commonMistakes: {
      'x=3': { misconceptionKey: 'sign-drop', label: 'Dropped the negative when dividing.' },
      'x = 3': { misconceptionKey: 'sign-drop', label: 'Dropped the negative when dividing.' },
    },
  },
  {
    id: 'two-step-2',
    topic: 'Two-step equations',
    gradeBand: 'Grades 7–8',
    difficulty: 'easy',
    prompt: 'Solve for x:',
    raw: '3x − 4 = 11',
    latex: '3x - 4 = 11',
    goal: 'Find the value of x.',
    opening: 'Same idea, different numbers — what operation would you undo first here?',
    steps: ['3x − 4 = 11', '3x = 15', 'x = 5'],
    stepsLatex: ['3x - 4 = 11', '3x = 15', 'x = 5'],
    stepNotes: [null, 'Added 4 to both sides.', 'Divided both sides by 3.'],
    hints: [
      [
        'What’s being subtracted from the x-term? Undo that first.',
        'Add 4 to both sides of the equation.',
        'Add 4 to both sides: $3x = 15$',
      ],
      [
        'You’re one operation away — the x still has a coefficient in front of it.',
        'Divide both sides by 3.',
        'Divide both sides by 3: $x = 5$',
      ],
    ],
    commonMistakes: {
      'x=15': { misconceptionKey: 'skip-last-step', label: 'Stopped before dividing by 3.' },
    },
  },
  {
    id: 'distributing-1',
    topic: 'Distributing',
    gradeBand: 'Grades 8–9',
    difficulty: 'hard',
    prompt: 'Solve for x:',
    raw: '2(x + 3) = 16',
    latex: '2(x + 3) = 16',
    goal: 'Find the value of x.',
    opening: 'There’s a term outside the parentheses touching everything inside. What do you do with it first?',
    steps: ['2(x + 3) = 16', '2x + 6 = 16', '2x = 10', 'x = 5'],
    stepsLatex: ['2(x + 3) = 16', '2x + 6 = 16', '2x = 10', 'x = 5'],
    stepNotes: [null, 'Distributed the 2 across both terms.', 'Subtracted 6 from both sides.', 'Divided both sides by 2.'],
    hints: [
      [
        'The 2 outside the parentheses applies to everything inside, not just the first term.',
        'Multiply the 2 by both x and 3.',
        'Distribute the 2: $2x + 6 = 16$',
      ],
      [
        'Now it looks like a familiar two-step equation. What’s being added to the x-term?',
        'Subtract 6 from both sides.',
        'Subtract 6 from both sides: $2x = 10$',
      ],
      [
        'One operation left standing between 2x and x.',
        'Divide both sides by 2.',
        'Divide both sides by 2: $x = 5$',
      ],
    ],
    commonMistakes: {
      '2x+3=16': { misconceptionKey: 'partial-distribute', label: 'Only distributed to the first term.' },
    },
  },
];

const TOPICS = [...new Set(DEMO_PROBLEMS.map((p) => p.topic))];

export function pickProblem(index = 0) {
  return DEMO_PROBLEMS[index % DEMO_PROBLEMS.length];
}

export function problemsByTopic() {
  return TOPICS.map((topic) => ({ topic, problems: DEMO_PROBLEMS.filter((p) => p.topic === topic) }));
}

/** Finds a bank problem matching free-typed input, or null if nothing matches closely. */
export function findProblemByText(text) {
  const norm = normalize(text);
  return DEMO_PROBLEMS.find((p) => normalize(p.raw) === norm || normalize(`${p.prompt}${p.raw}`) === norm) || null;
}

function normalize(line) {
  return (line || '').replace(/\s+/g, '').toLowerCase();
}

// ─────────────────────────────────────────────────────────────────────────
// Offline mirrors of the start-problem / check-step / hint endpoints.
// ─────────────────────────────────────────────────────────────────────────

/** Mirrors POST /api/agent-tutor/start-problem's response shape. */
export function startProblemLocal(rawInput) {
  const match = findProblemByText(rawInput) || DEMO_PROBLEMS[0];
  return {
    problem: {
      raw: match.raw,
      display: `${match.prompt} ${match.raw}`,
      latex: match.latex,
      topic: match.topic,
      difficulty: match.difficulty,
      supported: true,
      reason: '', // matches the live endpoint, which always sends "" (never null) when supported
      _bankId: match.id, // internal — lets local checkStep re-find the bank entry
    },
    goal: match.goal,
    expectedSteps: match.steps.length - 1,
    opening: match.opening,
    starterHint: match.hints[0]?.[0] || null,
  };
}

function bankFor(problem) {
  return DEMO_PROBLEMS.find((p) => p.id === problem?._bankId) || findProblemByText(problem?.raw) || DEMO_PROBLEMS[0];
}

/** Mirrors POST /api/agent-tutor/check-step's response shape. */
export function checkStepLocal(problem, stepsSoFar, studentStep) {
  const bank = bankFor(problem);
  // `stepsSoFar` is every committed attempt, right or wrong — progression
  // through the bank only advances on a correct verdict, so count those,
  // not raw attempts (a wrong attempt must not skip the expected step).
  const correctSoFar = stepsSoFar.filter((s) => s.verdict === 'correct').length;
  const stepIndex = correctSoFar + 1; // steps[0] is the given problem
  const expected = bank.steps[stepIndex];
  const normalizedSubmitted = normalize(studentStep);

  if (!expected) {
    return { verdict: 'incorrect', isFinal: false, solved: false, feedback: 'This problem is already solved — try "New problem".', stepLatex: null, misconception: null, nextPrompt: null, hint: null };
  }

  if (normalizedSubmitted === normalize(expected)) {
    const isFinal = stepIndex === bank.steps.length - 1;
    return {
      verdict: 'correct',
      isFinal,
      solved: isFinal,
      feedback: bank.stepNotes[stepIndex] || 'Correct.',
      stepLatex: bank.stepsLatex[stepIndex],
      misconception: null,
      nextPrompt: isFinal ? null : 'Nice — what’s the next move?',
      hint: null,
    };
  }

  const known = bank.commonMistakes[normalizedSubmitted];
  if (known) {
    return {
      verdict: 'partial',
      isFinal: false,
      solved: false,
      feedback: known.label,
      stepLatex: null,
      misconception: known.misconceptionKey,
      nextPrompt: 'Take another look at that step.',
      hint: null,
    };
  }

  return {
    verdict: 'incorrect',
    isFinal: false,
    solved: false,
    feedback: "That doesn't match where this problem is headed yet — check your last operation.",
    stepLatex: null,
    misconception: null,
    nextPrompt: null,
    hint: null,
  };
}

/** Mirrors POST /api/agent-tutor/hint's response shape. `level` is 1|2|3. */
export function hintLocal(problem, stepsSoFar, level) {
  const bank = bankFor(problem);
  // Same correct-only counting as checkStepLocal — the hint ladder tracks
  // the step the student is actually working toward, not attempt count.
  const stepIndex = stepsSoFar.filter((s) => s.verdict === 'correct').length; // 0-based index into bank.hints
  const ladder = bank.hints[stepIndex] || bank.hints[bank.hints.length - 1] || [];
  const clamped = Math.min(level, ladder.length) - 1;
  const text = ladder[Math.max(0, clamped)] || 'Try re-reading the last correct step and ask what operation would isolate x further.';
  return {
    level,
    hint: text,
    hintLatex: null,
    isFullStep: level >= 3,
  };
}

// Kept for any lightweight offline testing against the old two-argument
// shape; no longer on the live request path (see checkStepLocal above).
export function checkStep(problem, stepIndex, submitted) {
  const expected = problem.steps[stepIndex];
  const normalizedSubmitted = normalize(submitted);
  const normalizedExpected = normalize(expected);

  if (normalizedSubmitted === normalizedExpected) {
    return { correct: true, note: problem.stepNotes[stepIndex] || null };
  }

  const known = problem.commonMistakes[normalizedSubmitted];
  return {
    correct: false,
    note: null,
    misconception: known || null,
  };
}
